import { useEffect, useRef, useState } from "react";

import { TOKEN_KEY } from "@/lib/api";
import { forwardEventActivity } from "@/lib/activity";
import { useAuth } from "@/context/AuthContext";

/*
 * WebSocket hooks with auto-reconnect (exponential backoff) and server-time sync.
 * The server is the single source of truth for time: every message carrying
 * `server_time` refreshes the client offset used by countdowns.
 */

const buildUrl = (path) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
  const token = localStorage.getItem(TOKEN_KEY);
  return `${base}/api${path}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
};

export const useSocket = (path, onMessage, enabled = true) => {
  const [connected, setConnected] = useState(false);
  const [watchers, setWatchers] = useState(0);
  const offsetRef = useRef(0);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!enabled || !path) return undefined;
    let ws;
    let closed = false;
    let retry = 0;
    let pingTimer;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(buildUrl(path));
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        pingTimer = setInterval(() => {
          try {
            ws.send("ping");
          } catch {
            /* noop */
          }
        }, 25000);
      };
      ws.onmessage = (e) => {
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        if (msg.server_time) {
          offsetRef.current = new Date(msg.server_time).getTime() - Date.now();
        }
        if (msg.type === "hello" || msg.type === "presence") {
          setWatchers(msg.watchers ?? 0);
        }
        handlerRef.current?.(msg);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimer);
        if (!closed) {
          reconnectTimer = setTimeout(connect, Math.min(8000, 1000 * 2 ** retry));
          retry += 1;
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      try {
        if (ws.readyState === WebSocket.CONNECTING) {
          // avoid the noisy "closed before established" console warning
          ws.onopen = () => ws.close();
        } else {
          ws.close();
        }
      } catch {
        /* noop */
      }
    };
  }, [path, enabled]);

  return { connected, watchers, offsetRef };
};

/** Auction room for one event: lot updates, extensions, closings, presence.
    Every message also feeds the floating Activity Center (lib/activity). */
export const useEventSocket = (eventId, onMessage) =>
  useSocket(
    eventId ? `/ws/events/${eventId}` : null,
    (msg) => {
      forwardEventActivity(msg);
      onMessage?.(msg);
    },
    Boolean(eventId)
  );

/** Private user channel: notifications, organizer live feeds.
    Guests never attempt the socket (avoids 403 console noise + retry loop). */
export const useUserSocket = (onMessage, enabled = true) => {
  const { user } = useAuth();
  return useSocket("/ws/me", onMessage, enabled && Boolean(user));
};

/** Server-synced countdown. `offsetRef` comes from useSocket (server_time - local time). */
export const useServerCountdown = (target, offsetRef) => {
  const [parts, setParts] = useState(() => compute(target, offsetRef?.current || 0));
  useEffect(() => {
    const id = setInterval(() => setParts(compute(target, offsetRef?.current || 0)), 500);
    return () => clearInterval(id);
  }, [target, offsetRef]);
  return parts;
};

function compute(target, offset) {
  if (!target) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0, urgent: false };
  const now = Date.now() + offset;
  const total = Math.max(0, new Date(target).getTime() - now);
  return {
    total,
    days: Math.floor(total / 86400000),
    hours: Math.floor((total / 3600000) % 24),
    minutes: Math.floor((total / 60000) % 60),
    seconds: Math.floor((total / 1000) % 60),
    urgent: total > 0 && total < 5 * 60000,
  };
}
